import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, X, Send, Loader2 } from 'lucide-react';
import { useMP3Recorder } from '@/hooks/useMP3Recorder';
import { useMicrophonePermission } from '@/hooks/useMicrophonePermission';
import { cn } from '@/lib/utils';

interface AudioRecorderProps {
  onAudioReady: (audioBlob: Blob) => Promise<void>;
  disabled?: boolean;
  className?: string;
}

export function AudioRecorder({ onAudioReady, disabled, className }: AudioRecorderProps) {
  const [isSending, setIsSending] = useState(false);
  const { 
    isRecording, 
    duration, 
    error,
    startRecording, 
    stopRecording, 
    cancelRecording 
  } = useMP3Recorder();
  
  const { 
    permissionState, 
    ensureMicrophoneAccess 
  } = useMicrophonePermission();

  // Format duration as MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle mic button click
  const handleMicClick = useCallback(async () => {
    if (isRecording) return;
    
    // Check/request permission first
    const hasAccess = await ensureMicrophoneAccess();
    if (!hasAccess) {
      return;
    }
    
    await startRecording();
  }, [isRecording, ensureMicrophoneAccess, startRecording]);

  // Handle send
  const handleSend = useCallback(async () => {
    if (!isRecording || isSending) return;
    
    setIsSending(true);
    try {
      const blob = await stopRecording();
      if (blob && blob.size > 0) {
        await onAudioReady(blob);
      }
    } catch (err) {
      console.error('Error sending audio:', err);
    } finally {
      setIsSending(false);
    }
  }, [isRecording, isSending, stopRecording, onAudioReady]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    cancelRecording();
  }, [cancelRecording]);

  // If recording, show the recording UI
  if (isRecording) {
    return (
      <div className={cn(
        "flex items-center gap-2 bg-destructive/10 rounded-full px-3 py-1.5 animate-in fade-in slide-in-from-right-2 duration-200",
        className
      )}>
        {/* Cancel button */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleCancel}
          disabled={isSending}
          className="h-9 w-9 rounded-full text-destructive hover:bg-destructive/20 hover:text-destructive"
        >
          <X className="h-5 w-5" />
        </Button>

        {/* Recording indicator */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-destructive"></span>
          </span>
          <span className="text-sm font-medium text-destructive whitespace-nowrap">
            {formatDuration(duration)}
          </span>
        </div>

        {/* Send button */}
        <Button
          type="button"
          variant="default"
          size="icon"
          onClick={handleSend}
          disabled={isSending || duration < 1}
          className="h-9 w-9 rounded-full bg-primary hover:bg-primary/90"
        >
          {isSending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Send className="h-5 w-5" />
          )}
        </Button>
      </div>
    );
  }

  // Show error if any
  if (error) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <span className="text-xs text-destructive truncate max-w-[150px]">{error}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleMicClick}
          disabled={disabled || permissionState === 'denied'}
          className="h-10 w-10 rounded-full hover:bg-muted flex-shrink-0"
        >
          <Mic className="h-5 w-5 text-muted-foreground" />
        </Button>
      </div>
    );
  }

  // Default state - just the mic button
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={handleMicClick}
      disabled={disabled || permissionState === 'denied'}
      className={cn(
        "h-10 w-10 rounded-full hover:bg-muted flex-shrink-0 transition-colors",
        permissionState === 'denied' && "opacity-50 cursor-not-allowed",
        className
      )}
      title={permissionState === 'denied' ? 'Permissão de microfone negada' : 'Gravar áudio'}
    >
      <Mic className="h-5 w-5 text-muted-foreground" />
    </Button>
  );
}
